import { create } from 'zustand';
import { hostingAPI } from '../lib/api';
import { getChannel, joinHostingChannel, hostingPush, leaveHostingChannel } from '../lib/realtime';

// ── Types ────────────────────────────────────────────────────────────────────

export interface HostingPlan {
  id: string;
  name: string;
  display_name: string;
  memory_mb: number;
  disk_mb: number;
  cpu_limit: number;
  max_backups: number;
  max_servers_per_user: number;
  price_monthly: number;
  is_active: boolean;
}

export type HostedServerStatus =
  | 'installing'
  | 'offline'
  | 'starting'
  | 'online'
  | 'stopping'
  | 'suspended'
  | 'hibernated';

export type ServerType = 'vanilla' | 'paper' | 'spigot' | 'forge' | 'fabric' | 'purpur';

export interface HostedServer {
  id: string;
  owner_id: string;
  erozion_server_id: string | null;
  plan_id: string;
  pterodactyl_id: number | null;
  pterodactyl_identifier: string | null;
  pterodactyl_uuid: string | null;
  name: string;
  server_type: ServerType;
  mc_version: string;
  subdomain: string | null;
  status: HostedServerStatus;
  memory_mb: number;
  disk_mb: number;
  cpu_limit: number;
  allocation_ip: string | null;
  allocation_port: number | null;
  node_id: number | null;
  max_players: number;
  description: string | null;
  created_at: string;
  updated_at: string;
  last_active_at: string | null;
  eula_accepted?: boolean;
  owner_username?: string;
  my_permissions?: number;
  subusers?: HostedServerSubuser[];
}

export interface HostedServerSubuser {
  id: string;
  user_id: string;
  username: string;
  avatar_url: string | null;
  permissions: number;
  created_at: string;
}

export interface HostedServerBackup {
  id: string;
  server_id: string;
  pterodactyl_backup_uuid: string | null;
  name: string;
  size_bytes: number;
  status: 'creating' | 'completed' | 'failed' | 'deleted';
  created_at: string;
  completed_at: string | null;
}

export interface ServerStats {
  memoryBytes?: number;
  cpuAbsolute?: number;
  diskBytes?: number;
  networkRxBytes?: number;
  networkTxBytes?: number;
  state?: string;
  uptime?: number;
}

export interface PteroEgg {
  id: number;
  name: string;
  description: string;
  dockerImages: string[];
}

export interface PteroLocation {
  id: number;
  short: string;
  long: string;
}

// ── Permission bitflags (must match server-side HostingPermissions) ──────────

export const HostingPermissions = {
  CONSOLE:  1 << 0,
  POWER:    1 << 1,
  BACKUPS:  1 << 2,
  SETTINGS: 1 << 3,
  SUBUSERS: 1 << 4,
  PLUGINS:  1 << 5,
  FILES:    1 << 6,
} as const;

// ── Store ────────────────────────────────────────────────────────────────────

interface HostingState {
  // Data
  plans: HostingPlan[];
  eggs: PteroEgg[];
  locations: PteroLocation[];
  ownedServers: HostedServer[];
  sharedServers: HostedServer[];
  currentServer: HostedServer | null;
  backups: HostedServerBackup[];
  consoleLogs: string[];
  stats: ServerStats | null;

  // UI state
  isLoading: boolean;
  error: string | null;
  isConsoleConnected: boolean;

  // Actions — data
  fetchPlans: () => Promise<void>;
  fetchEggs: () => Promise<void>;
  fetchLocations: () => Promise<void>;
  fetchServers: () => Promise<void>;
  fetchServer: (id: string) => Promise<void>;
  createServer: (data: { name: string; serverType: string; mcVersion: string; planId?: string; description?: string; erozionServerId?: string; eggId?: number; locationId?: number; eulaAccepted?: boolean }) => Promise<string | null>;
  updateServer: (id: string, data: { name?: string; description?: string; maxPlayers?: number; erozionServerId?: string | null }) => Promise<void>;
  deleteServer: (id: string) => Promise<void>;
  setCurrentServer: (server: HostedServer | null) => void;

  // Actions — power
  sendPower: (id: string, action: 'start' | 'stop' | 'restart' | 'kill') => Promise<void>;

  // Actions — console (Socket.IO)
  connectConsole: (serverId: string) => void;
  disconnectConsole: (serverId: string) => void;
  sendConsoleCommand: (serverId: string, command: string) => void;
  clearConsoleLogs: () => void;
  _keepAliveInterval: ReturnType<typeof setInterval> | null;

  // Actions — backups
  fetchBackups: (serverId: string) => Promise<void>;
  createBackup: (serverId: string, name?: string) => Promise<void>;
  deleteBackup: (serverId: string, backupId: string) => Promise<void>;

  // Actions — subusers
  fetchSubusers: (serverId: string) => Promise<void>;
  addSubuser: (serverId: string, userId: string, permissions: number) => Promise<boolean>;
  updateSubuserPermissions: (serverId: string, subuserId: string, permissions: number) => Promise<void>;
  removeSubuser: (serverId: string, subuserId: string) => Promise<void>;

  // Actions — EULA
  acceptEula: (serverId: string) => Promise<boolean>;

  // Internal — socket listeners
  _initSocketListeners: () => void;
  _cleanupSocketListeners: () => void;
}

export const useHostingStore = create<HostingState>((set, get) => ({
  plans: [],
  eggs: [],
  locations: [],
  ownedServers: [],
  sharedServers: [],
  currentServer: null,
  backups: [],
  consoleLogs: [],
  stats: null,
  isLoading: false,
  error: null,
  isConsoleConnected: false,
  _keepAliveInterval: null,

  // ── Fetch plans ──────────────────────────────────────────────────────────
  fetchPlans: async () => {
    try {
      const plans = await hostingAPI.getPlans();
      set({ plans });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  // ── Fetch eggs (from Pterodactyl) ───────────────────────────────────────
  fetchEggs: async () => {
    try {
      const eggs = await hostingAPI.getEggs();
      set({ eggs });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  // ── Fetch locations (from Pterodactyl) ──────────────────────────────────
  fetchLocations: async () => {
    try {
      const locations = await hostingAPI.getLocations();
      set({ locations });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  // ── Fetch user's servers ─────────────────────────────────────────────────
  fetchServers: async () => {
    try {
      set({ isLoading: true, error: null });
      const { owned, shared } = await hostingAPI.getServers();
      set({ ownedServers: owned, sharedServers: shared });
    } catch (err: any) {
      set({ error: err.message });
    } finally {
      set({ isLoading: false });
    }
  },

  // ── Fetch single server detail ──────────────────────────────────────────
  fetchServer: async (id) => {
    try {
      set({ isLoading: true, error: null });
      const server = await hostingAPI.getServer(id);
      set({ currentServer: server });
    } catch (err: any) {
      set({ error: err.message });
    } finally {
      set({ isLoading: false });
    }
  },

  // ── Create server ───────────────────────────────────────────────────────
  createServer: async (data) => {
    try {
      set({ isLoading: true, error: null });
      const result = await hostingAPI.createServer(data);
      // Refresh list
      await get().fetchServers();
      return result.id;
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
      return null;
    } finally {
      set({ isLoading: false });
    }
  },

  // ── Update server ──────────────────────────────────────────────────────
  updateServer: async (id, data) => {
    try {
      const updated = await hostingAPI.updateServer(id, data);
      // Update in local state
      set((state) => ({
        ownedServers: state.ownedServers.map((s) => (s.id === id ? { ...s, ...updated } : s)),
        currentServer: state.currentServer?.id === id ? { ...state.currentServer, ...updated } : state.currentServer,
      }));
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
    }
  },

  // ── Delete server ──────────────────────────────────────────────────────
  deleteServer: async (id) => {
    try {
      await hostingAPI.deleteServer(id);
      set((state) => ({
        ownedServers: state.ownedServers.filter((s) => s.id !== id),
        currentServer: state.currentServer?.id === id ? null : state.currentServer,
      }));
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
    }
  },

  setCurrentServer: (server) => set({ currentServer: server, consoleLogs: [], stats: null, isConsoleConnected: false }),

  // ── Power action ───────────────────────────────────────────────────────
  sendPower: async (id, action) => {
    try {
      await hostingAPI.power(id, action);
      // Optimistic status update
      const statusMap: Record<string, HostedServerStatus> = {
        start: 'starting',
        stop: 'stopping',
        restart: 'starting',
        kill: 'offline',
      };
      set((state) => ({
        ownedServers: state.ownedServers.map((s) =>
          s.id === id ? { ...s, status: statusMap[action] } : s,
        ),
        currentServer:
          state.currentServer?.id === id
            ? { ...state.currentServer, status: statusMap[action] }
            : state.currentServer,
      }));
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
    }
  },

  // ── Console via Phoenix Channel ─────────────────────────────────────────
  connectConsole: (serverId) => {
    const ch = joinHostingChannel(serverId);
    if (!ch) {
      console.warn('[hostingStore] connectConsole: no channel');
      return;
    }

    ch.push('console_connect', { serverId });
    set({ isConsoleConnected: false });

    // Start keep-alive every 30s to extend the backend WS session TTL
    const prev = get()._keepAliveInterval;
    if (prev) clearInterval(prev);
    const interval = setInterval(() => {
      const channel = getChannel(`hosting:${serverId}`);
      if (channel) {
        channel.push('keep_alive', { serverId });
      }
    }, 30_000);
    set({ _keepAliveInterval: interval });
  },

  disconnectConsole: (serverId) => {
    // Stop keep-alive
    const interval = get()._keepAliveInterval;
    if (interval) {
      clearInterval(interval);
      set({ _keepAliveInterval: null });
    }

    hostingPush(serverId, 'console_disconnect', { serverId });
    set({ isConsoleConnected: false });
  },

  sendConsoleCommand: (serverId, command) => {
    hostingPush(serverId, 'console_command', { serverId, command });
    // Echo the command locally
    set((state) => ({
      consoleLogs: [...state.consoleLogs, `> ${command}`],
    }));
  },

  clearConsoleLogs: () => set({ consoleLogs: [] }),

  // ── Backups ────────────────────────────────────────────────────────────
  fetchBackups: async (serverId) => {
    try {
      const backups = await hostingAPI.getBackups(serverId);
      set({ backups });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  createBackup: async (serverId, name) => {
    try {
      await hostingAPI.createBackup(serverId, name);
      // Will be updated via socket event when complete; refetch for 'creating' status
      await get().fetchBackups(serverId);
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
    }
  },

  deleteBackup: async (serverId, backupId) => {
    try {
      await hostingAPI.deleteBackup(serverId, backupId);
      set((state) => ({
        backups: state.backups.filter((b) => b.id !== backupId),
      }));
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  // ── Subusers ───────────────────────────────────────────────────────────
  fetchSubusers: async (serverId) => {
    try {
      const subusers = await hostingAPI.getSubusers(serverId);
      set((state) => ({
        currentServer: state.currentServer?.id === serverId
          ? { ...state.currentServer, subusers }
          : state.currentServer,
      }));
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  addSubuser: async (serverId, userId, permissions) => {
    try {
      await hostingAPI.addSubuser(serverId, userId, permissions);
      await get().fetchSubusers(serverId);
      return true;
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
      return false;
    }
  },

  updateSubuserPermissions: async (serverId, subuserId, permissions) => {
    try {
      await hostingAPI.updateSubuser(serverId, subuserId, permissions);
      await get().fetchSubusers(serverId);
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
    }
  },

  removeSubuser: async (serverId, subuserId) => {
    try {
      await hostingAPI.removeSubuser(serverId, subuserId);
      set((state) => ({
        currentServer: state.currentServer
          ? {
              ...state.currentServer,
              subusers: state.currentServer.subusers?.filter((s) => s.id !== subuserId),
            }
          : null,
      }));
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
    }
  },

  // ── EULA ───────────────────────────────────────────────────────────────
  acceptEula: async (serverId) => {
    try {
      await hostingAPI.acceptEula(serverId);
      set((state) => ({
        currentServer: state.currentServer?.id === serverId
          ? { ...state.currentServer, eula_accepted: true }
          : state.currentServer,
      }));
      return true;
    } catch (err: any) {
      set({ error: err.response?.data?.message || err.message });
      return false;
    }
  },

  // ── Channel listeners (call once from component mount) ──────────────
  _initSocketListeners: () => {
    // We need a hosting channel for the current server
    const serverId = get().currentServer?.id;
    if (!serverId) return;

    const channel = getChannel(`hosting:${serverId}`);
    if (!channel) return;

    channel.on('console_connected', () => {
      set({ isConsoleConnected: true });
    });

    channel.on('console_output', ({ output }: { serverId: string; output: string }) => {
      set((state) => ({
        consoleLogs: [...state.consoleLogs.slice(-500), output],
      }));
    });

    channel.on('daemon_message', ({ output }: { serverId: string; output: string }) => {
      set((state) => ({
        consoleLogs: [...state.consoleLogs.slice(-500), `[Erozion Go] ${output}`],
      }));
    });

    channel.on('stats_update', ({ stats }: { serverId: string; stats: ServerStats }) => {
      set({ stats });
    });

    channel.on('status_update', ({ serverId: sid, status }: { serverId: string; status: string }) => {
      const dbStatus = mapWsStatus(status);
      set((state) => ({
        ownedServers: state.ownedServers.map((s) =>
          s.id === sid ? { ...s, status: dbStatus } : s,
        ),
        sharedServers: state.sharedServers.map((s) =>
          s.id === sid ? { ...s, status: dbStatus } : s,
        ),
        currentServer:
          state.currentServer?.id === sid
            ? { ...state.currentServer, status: dbStatus }
            : state.currentServer,
      }));
    });

    channel.on('server_ready', ({ serverId: sid }: { serverId: string }) => {
      get().fetchServers();
      if (get().currentServer?.id === sid) get().fetchServer(sid);
    });

    channel.on('server_error', ({ message }: { serverId: string; message: string }) => {
      set({ error: message });
    });

    channel.on('backup_complete', ({ serverId: sid }: { serverId: string; backupId: string }) => {
      if (get().currentServer?.id === sid) get().fetchBackups(sid);
    });

    channel.on('console_error', ({ message }: { serverId: string; message: string }) => {
      set((state) => ({
        consoleLogs: [...state.consoleLogs, `[Error] ${message}`],
      }));
    });

    channel.on('install_output', ({ output }: { serverId: string; output: string }) => {
      set((state) => ({
        consoleLogs: [...state.consoleLogs.slice(-500), `[Install] ${output}`],
      }));
    });

    channel.on('install_complete', () => {
      set((state) => ({
        consoleLogs: [...state.consoleLogs, '[Install] Installation complete!'],
      }));
      get().fetchServers();
    });
  },

  _cleanupSocketListeners: () => {
    const serverId = get().currentServer?.id;
    if (!serverId) return;
    // Leaving the hosting channel removes all listeners
    leaveHostingChannel(serverId);
  },
}));

function mapWsStatus(status: string): HostedServerStatus {
  switch (status) {
    case 'running': return 'online';
    case 'starting': return 'starting';
    case 'stopping': return 'stopping';
    case 'offline': return 'offline';
    default: return 'offline';
  }
}
